import React, { useEffect, useState, useRef, useLayoutEffect, useMemo, JSX } from 'react';
import { all } from '@awesome.me/kit-2cbcf7d4a0/icons';
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Chip } from '@mui/material';
import { filterBy, orderBy, process, CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { DetailExpandDescriptor, HeaderThElement } from '@progress/kendo-react-data-tools';
import {
    Grid,
    GridColumn,
    GridColumnMenuWrapper,
    GridColumnProps,
    GridColumnReorderEvent,
    GridEvent,
    GridColumnMenuProps,
    GridCustomHeaderCellProps,
    GridCustomCellProps,
    GridHandle,
} from '@progress/kendo-react-grid';
import { Checkbox } from '@progress/kendo-react-inputs';
import { Tooltip } from '@progress/kendo-react-tooltip';
import _ from 'lodash';
import { calculateHierarchyRowWidth, CustomRow } from '@components/core/grid-helper';
import { cellRender, setChildWidth, setWidth, configureColumnsArray, showAppliedFilters } from './grid.service';
import IExpandableGridProps, { IExpandableGridColumn } from './interfaces/grid-props.interface';
import NavigationService from '@components-clean/page-nav/navigation.service';
import { useTranslation, useBoolean } from 'hooks';
import Constants from 'utils/constants';
import { compareColumnsByOrderIndex, prepareForGrouping } from 'utils/grid';
import ActionsMenu from 'views/components/core/actions-menu';
import CustomColumnMenu from 'views/components/core/custom-column-menu';
import HeaderOptions from 'views/components/core/header-options';
import Pager from 'views/components/core/pager';
import './styles.scss';
import { addDetailRowIdToGrid, textToId } from '@utils/automation-test-helpers';

library.add({ ...all });

const ExpandableGrid = ({
    id,
    items,
    columns,
    detail,
    hasDetail,
    options,
    groupable,
    scrollable,
    actions,
    showPager,
    maxHeight,
    rowActions,
    reorderable,
    resizable,
    pageable,
    excelExportOptions,
    customKey,
    initialFilter,
    initialSort,
    showCheckbox,
    setOwner,
    detailClass,
    addScroll,
    hideColumnMenu,
    hideClearFilter,
    isChildGrid,
    showFirstColumn = true,
    isModalOpened = false,
    removeHideFilter,
    onUpdateAmount,
}: IExpandableGridProps) => {
    const { t } = useTranslation();
    const widthFirstExtraColumn = showFirstColumn ? 43 : 0;

    let grid: any = null;
    const generalRef = useRef<GridHandle | null>(null);
    const exportRef = useRef(null);

    const [pageSize, setPageSize] = useState(5);
    const [columnsArray, setColumnsArray] = useState([]);
    const [settingsArray, setSettingsArray] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pagingState, setPagingState] = useState({ skip: 0, take: pageSize });
    const [expandedItems, setExpandedItems] = React.useState<DetailExpandDescriptor>({});
    const [checkedRows, setCheckedRows] = useState([] as Array<any>);
    const [gridCurrent, setGridCurrent] = useState(0);
    const [minGridWidth, setMinGridWidth] = useState(0);
    const [filteredItems, setFilteredItems] = useState([]);
    const [gridKeyAffix, setGridKeyAffix] = useState<number>(1);
    const showScroll = useBoolean(addScroll);

    useEffect(() => {
        if (items && (currentPage - 1) * pageSize > items.length) setCurrentPage(1);
    }, [items]);

    const isOpenSideBar = useBoolean(NavigationService.getSlideBarStatus());

    const [toShow, setToShow] = useState([]);
    const [lockedColumns, setLockedColumns] = useState([]);
    // used if showPager is true
    const [data, setData] = useState([]);
    // used if showPager is false
    const [allData, setAllData] = useState([]);
    const [filter, setFilter] = useState(null as CompositeFilterDescriptor);
    const [sort, setSort] = useState(initialSort || []);
    const [group, setGroup] = useState([]);
    const setMinWidth = useBoolean(false);
    const isLoadedInitFilter = useBoolean(false);

    const gridElement: any = isChildGrid
        ? document.querySelector('.child-grid-wrapper .expandable-grid')
        : document.querySelector(`.${customKey}`);
    const element = document.getElementsByClassName('k-virtual-content')[0];

    useEffect(() => {
        const subscribe = NavigationService.onUpdateSideBar().subscribe((isOpen: boolean) => {
            isOpenSideBar.setValue(isOpen);
        });
        return () => {
            subscribe.unsubscribe();
        };
    }, []);

    useEffect(() => {
        // this functionality is turned off for now.
        // It should take saved settings for hidden/locked column from localStorage or remove server in future
        setLockedColumns(JSON.parse(window.localStorage.getItem(`${customKey}-lock`)) || []);
        setToShow(JSON.parse(window.localStorage.getItem(`${customKey}-to-show`)) || []);
    }, []);

    useEffect(() => {
        setFilteredItems((items && filterBy(items.slice(0), filter)) || []);
    }, [filter, items]);

    useEffect(() => {
        if (checkedRows.length) setCheckedRows(data.filter((item: any) => item.checked));
    }, [data]);

    useLayoutEffect(() => {
        handleResize();
    }, [group, columnsArray]);

    useEffect(() => {
        // small optimization tip. to not change column array if modal is opened
        if (!isModalOpened) {
            const newColumns = configureColumnsArray(columns, toShow, settingsArray, lockedColumns);
            setColumnsArray(newColumns);
        }
    }, [columns]);

    useEffect(() => {
        setAllData(items);
    }, [items]);

    // When sort, paging, filter, or items change, re-process the data
    useEffect(() => {
        const result = prepareForGrouping(filteredItems, columnsArray, group);

        if (onUpdateAmount) onUpdateAmount(result.preparedArray.length);

        setAllData(
            process(orderBy(result.preparedArray, sort), {
                group: result.grouping,
            }).data,
        );

        setData(
            process(orderBy(result.preparedArray, sort), {
                ...pagingState,
                group: result.grouping,
            }).data,
        );
    }, [pagingState, sort, filteredItems, group]);

    // Update the grid data when the current page changes
    useEffect(() => {
        setPagingState({ skip: (currentPage - 1) * pageSize, take: pageSize });
    }, [currentPage]);

    // Update the amount of items per page
    useEffect(() => {
        setPagingState({ skip: 0, take: pageSize });
    }, [pageSize]);

    useEffect(() => {
        // isLoadedInitFilter trigger is used to apply initial filter only on initial rendering
        if ((data?.length || allData?.length) && initialFilter && !isLoadedInitFilter.value) {
            isLoadedInitFilter.setTrue();
            setFilter(initialFilter);
        }
    }, [initialFilter, data, allData]);

    useEffect(() => {
        window.addEventListener('resize', () => {
            debouncedHandleShowScroll();
        });

        // set width of custom scroll
        const array = [...columnsArray].filter((item: any) => item.show);
        const value = array.reduce((sum, item) => sum + item.minWidth, 0) + widthFirstExtraColumn;

        setMinGridWidth(value);
        const gridElementWidth = gridElement?.offsetWidth;
        setGridCurrent(gridElementWidth);
        setMinWidth.setValue(gridElementWidth < value);
    }, [gridElement]);

    const expandChange = (event: any) => {
        event.dataItem.expanded =
            expandedItems[event.dataItem.id] !== undefined ? !expandedItems[event.dataItem.id] : !event.expanded;
        setExpanded(event.dataItem.id, event.dataItem.expanded);
    };

    const setExpanded = (id: string, expanded: boolean) => {
        const newExpandedItems: { [index: string]: any } = { ...expandedItems };
        newExpandedItems[id] = expanded;
        setExpandedItems(newExpandedItems);
    };

    const setAll = (b: boolean) => {
        const ex: any = { ...expandedItems };
        items.forEach((i) => (ex[i.id] = i.expanded = b));
        setExpandedItems(ex);
    };

    const isFiltered = (field: string) =>
        filter &&
        (filter.filters || []).find((f1: any) => f1.filters && f1.filters.find((f2: any) => f2.field === field));

    const appliedFilters = showAppliedFilters(filter, columns);

    const removeFilter = (f: any) => {
        const newFilter = { ...filter };
        newFilter.filters = newFilter.filters.filter((f1: any) => !f1.filters.find((f2: any) => f2.field === f));
        setFilter(newFilter);
    };

    const clearFilters = () => setFilter({ ...filter, filters: [] });

    const expand = () => setAll(true);
    const collapse = () => setAll(false);

    const dataStateChange = (event: any) => {
        setGroup(event.dataState.group);
        if (currentPage !== 1) setCurrentPage(1);
    };

    const handleChangeCheckedRows = (dataItem: any) => {
        const array = [...checkedRows];
        if (dataItem.checked) {
            array.push(dataItem);
            setCheckedRows(array);
        } else if (!dataItem.checked) {
            const index: number = array.findIndex((item: any) => dataItem.id === item.id);
            array.splice(index, 1);
            setCheckedRows(array);
        }
    };

    const onColumnsSubmit = (columnState: [IExpandableGridColumn]) => {
        setColumnsArray(columnState);
        setSettingsArray(columnState);
        setGridKeyAffix((value) => {
            return ++value;
        });
    };

    const gridKey = `${customKey}_${gridKeyAffix}`;

    const handleShowScroll = () => {
        const columnsContainer = document.querySelector('.k-grid-header-wrap table');

        // determine to show custom scroll or not
        showScroll.setValue(columnsContainer?.offsetWidth >= gridElement?.offsetWidth);
    };

    const debouncedHandleShowScroll = _.debounce(handleShowScroll, 300);

    const onColumnResize = (e: any) => {
        debouncedHandleShowScroll();
    };

    const getColumn = (inputColumns: any[], field: string) =>
        inputColumns.find((column: any) => column.field === field);

    const getColumnsFromReorderedGridColumns = (reorderedGridColumns: GridColumnProps[]) =>
        reorderedGridColumns.reduce((result: any[], column: GridColumnProps) => {
            const newColumn = getColumn([...columnsArray], column.field);

            if (newColumn)
                result.push({
                    ...newColumn,
                    orderIndex: column.orderIndex,
                });

            return result;
        }, []);

    const onColumnReorder = (e: GridColumnReorderEvent) => {
        const newColumns = getColumnsFromReorderedGridColumns(e.columns);

        setColumnsArray(newColumns);
        setGridKeyAffix((value) => {
            return ++value;
        });
    };

    const handleResize = () => {
        // recalculate if we need to show scroll on resize.
        // recalculate if we need to fit columns on all grid
        debouncedHandleShowScroll();
        const gridElementWidth = gridElement?.offsetWidth;

        if (gridElementWidth < minGridWidth && !setMinWidth.value) {
            setMinWidth.setTrue();
        } else if (gridElementWidth > minGridWidth) {
            setMinWidth.setFalse();
            setGridCurrent(gridElementWidth);
        }
    };

    const recordHasDetails = (item: any) => (hasDetail ? hasDetail(item) : detail ? detail(item) : null);

    const onGridScroll = (event: GridEvent) => {
        // prevent parent scrolling during child scrolling
        event.nativeEvent.stopPropagation();
    };

    // The following useEffect ensures that we give "data-testid"s to the appropriate expanded-to-show
    // "detail-rows".
    useEffect(() => {
        addDetailRowIdToGrid(generalRef);
        calculateHierarchyRowWidth(generalRef);
    });

    const getColumnMenu = (column: IExpandableGridColumn) => {
        return function CustomColumnMenuWrapper(columnMenuProps: GridColumnMenuProps) {
            return (
                <CustomColumnMenu
                    {...columnMenuProps}
                    gridKey={gridKey}
                    columns={columnsArray.sort(compareColumnsByOrderIndex)}
                    onColumnsSubmit={onColumnsSubmit}
                    filterUi={column.filterUi}
                    filterKey={column.filterKey}
                    removeHideFilter={removeHideFilter}
                    filter={filter}
                />
            );
        };
    };

    const columnMenus = useMemo(() => {
        const map = new Map<string, (props: GridColumnMenuProps) => JSX.Element>();
        columnsArray.forEach((column: IExpandableGridColumn) => {
            if (typeof column.field === 'string') {
                map.set(column.field, getColumnMenu(column));
            }
        });
        return map;
    }, [columnsArray, gridKey, filter, removeHideFilter]);

    return (
        <div
            className={`expandable-grid ${!items?.length ? 'overlay' : ''} ${customKey}`}
            key={customKey}
            data-testid={customKey}
        >
            {/* Disabled CustomScroll after kendo-grid-update */}
            {/* <CustomScroll */}
            {/*   show={addScroll && showScroll.value && element && data?.length && gridCurrent} */}
            {/*   element={element} */}
            {/*   columnsArray={columnsArray} */}
            {/* /> */}
            {!items || (items && !items.length) ? (
                <div className="overlay-without-records" style={{ maxHeight: maxHeight ? +maxHeight + 130 : '100%' }} />
            ) : null}
            {options ? (
                <div className="header-menu-grid">
                    <HeaderOptions
                        options={{
                            allowExpandCollapseAll: options ? options.allowExpandCollapseAll : true,
                            allowExcelExport: options.allowExcelExport,
                            expandedItems:
                                expandedItems &&
                                Object.keys(expandedItems).filter((key: string) => expandedItems[key] === true).length >
                                    0,
                            appliedFilters: appliedFilters && appliedFilters.length,
                        }}
                        excelExportOptions={{
                            exportData: allData,
                            fileName: excelExportOptions ? excelExportOptions.fileName : '',
                            name: excelExportOptions ? excelExportOptions.name : '',
                            columns: excelExportOptions?.columns || columnsArray,
                            mapColumns: excelExportOptions?.mapColumns,
                        }}
                        clearFilters={clearFilters}
                        expandAll={expand}
                        collapseAll={collapse}
                        actions={actions}
                        checkedRows={checkedRows}
                    />
                    {options.useFilterChips && appliedFilters && appliedFilters.length ? (
                        <div className="filters-applied">
                            {appliedFilters.map((appliedFilter: any, index: number) => (
                                <div
                                    className="selected-filter"
                                    key={appliedFilter.key}
                                    data-testid={`selected-filter-${textToId(appliedFilter.name, index)}`}
                                >
                                    <FontAwesomeIcon className="icon" icon={['fas', 'filter']} />
                                    <Chip
                                        className="filter-chip"
                                        label={appliedFilter.name}
                                        deleteIcon={<FontAwesomeIcon className="delete-icon" icon={['fal', 'times']} />}
                                        onDelete={() => removeFilter(appliedFilter.key)}
                                    />
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>
            ) : null}
            {showPager && (
                <Pager
                    page={currentPage}
                    amountOfPages={5}
                    pageSize={pageSize}
                    total={filteredItems && filteredItems.length}
                    onChange={(page: number) => setCurrentPage(page)}
                    onSelectPageSize={(amount: number) => setPageSize(amount)}
                />
            )}
            {!options && !hideClearFilter ? (
                <div className="actions-filters-wrapper">
                    {actions && actions.length && (
                        <>
                            <div className="action-items">
                                {actions.map((item: any, key: number) => (
                                    <div
                                        key={key}
                                        className={`action-item ${!item.enabled(checkedRows) ? 'disabled' : ''}`}
                                        onClick={() => {
                                            if (item.enabled(checkedRows)) item.onClick(checkedRows);
                                        }}
                                    >
                                        {item.icon && (
                                            <FontAwesomeIcon className="action-icon" icon={['fal', item.icon]} />
                                        )}
                                        <p className="action-label">{item.label}</p>
                                    </div>
                                ))}
                            </div>
                            <span>|</span>
                        </>
                    )}
                    <span className="filter-divider-child">
                        <span className={`clear-filters ${!appliedFilters ? 'disabled' : ''}`} onClick={clearFilters}>
                            <FontAwesomeIcon className="icon" icon={['fas', 'filter']} />
                            <p>{t('clearFilters')}</p>
                        </span>
                    </span>
                </div>
            ) : null}
            <Tooltip anchorElement="target" position="top">
                <Grid
                    rows={{ data: CustomRow }}
                    onColumnResize={onColumnResize}
                    onColumnReorder={onColumnReorder}
                    key={gridKey}
                    className={`grid-component ${!filteredItems?.length ? 'no-items' : ''} ${
                        !showFirstColumn ? 'hide-first-column' : ''
                    } 
                        ${detailClass} ${isOpenSideBar.value ? 'sidebar' : ''}`}
                    style={{ maxHeight }}
                    ref={(instance: any) => {
                        grid = instance;

                        if (id)
                            // This needs to be here to give external access to the Kendo grid -KM
                            (window as any)[id] = grid;

                        // Set generalRef
                        generalRef.current = grid;
                    }}
                    data={!showPager ? allData : data}
                    pageable={pageable}
                    {...pagingState}
                    reorderable={reorderable}
                    filterOperators={Constants.filterOperators}
                    groupable={groupable}
                    group={group}
                    sortable
                    resizable={resizable}
                    sort={sort}
                    scrollable={scrollable}
                    onSortChange={({ sort: newSort }: any) => setSort(newSort)}
                    onDataStateChange={dataStateChange}
                    filter={filter}
                    onFilterChange={({ filter: newFilter }: any) => {
                        setFilter(newFilter);
                        setCurrentPage(1);
                        setGridKeyAffix((value) => {
                            return ++value;
                        });
                    }}
                    cells={{
                        headerCell: (props) => {
                            return (
                                <HeaderThElement
                                    columnId={props.thProps?.columnId || ''}
                                    {...props.thProps}
                                    className={props.thProps?.className}
                                >
                                    <a className="header-cell">
                                        <span className="header-cell-title"></span>
                                    </a>
                                </HeaderThElement>
                            );
                        },
                        data: cellRender,
                        hierarchy: {
                            data: (props: GridCustomCellProps) => {
                                const elCount = [detail, rowActions, showCheckbox].filter(Boolean).length;
                                return (
                                    <td
                                        {...props.tdProps}
                                        className={`custom-cell k-grid-content-sticky ${
                                            elCount > 0 ? 'k-hierarchy-cell' : ''
                                        } ${elCount > 1 ? 'multiple-inputs' : ''}`}
                                        style={{ minHeight: '1px' }}
                                    >
                                        <div
                                            className={`custom-cell-flex-wrapper ${
                                                recordHasDetails(props.dataItem) ? 'with-details' : ''
                                            }
                                                    ${showCheckbox ? 'checkbox' : ''}`}
                                        >
                                            <div className="actions-checkbox">
                                                {rowActions && rowActions(props.dataItem) && (
                                                    <div
                                                        className={`actions-wrapper ${!detail ? 'without-detail' : ''}`}
                                                    >
                                                        <ActionsMenu
                                                            actions={rowActions(props.dataItem)}
                                                            customClassName="mt"
                                                            anchorOrigin={{
                                                                vertical: 'top',
                                                                horizontal: 'right',
                                                            }}
                                                            transformOrigin={{
                                                                vertical: 'top',
                                                                horizontal: 'left',
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                                {showCheckbox && (
                                                    <Checkbox
                                                        rounded={null}
                                                        className="cell-checkbox border-bottom"
                                                        checked={props.dataItem.checked}
                                                        onChange={() => {
                                                            if (setOwner) {
                                                                if (
                                                                    setOwner(
                                                                        props.dataItem.owner,
                                                                        checkedRows,
                                                                        !props.dataItem.checked,
                                                                    )
                                                                ) {
                                                                    props.dataItem.checked = !props.dataItem.checked;
                                                                    handleChangeCheckedRows(props.dataItem);
                                                                } else {
                                                                    const array = [...checkedRows];
                                                                    setCheckedRows(array);
                                                                }
                                                            } else {
                                                                props.dataItem.checked = !props.dataItem.checked;
                                                                handleChangeCheckedRows(props.dataItem);
                                                            }
                                                        }}
                                                        size="medium"
                                                    />
                                                )}
                                            </div>
                                            {recordHasDetails(props.dataItem) && (
                                                <div
                                                    className={`expand-btn ${
                                                        expandedItems[props.dataItem.id] ? 'expanded' : 'non-expanded'
                                                    }`}
                                                    onClick={() => {
                                                        expandChange(props);
                                                    }}
                                                    data-testid="expand-button"
                                                >
                                                    <FontAwesomeIcon
                                                        icon={['fal', 'chevron-down']}
                                                        className="icon down-icon"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                );
                            },
                        },
                    }}
                    dataItemKey="id"
                    detailExpand={expandedItems}
                    detail={
                        detail || rowActions || showCheckbox || reorderable
                            ? (props: any) =>
                                  detail && props.dataItem.id ? (
                                      <div className="detail-wrapper-grid">
                                          <div className="vertical-line" />
                                          <span className="divider-grid" />
                                          <div
                                              className="detail-element"
                                              key={props.dataItem.id}
                                              id={props.dataItem.id}
                                          >
                                              {detail(props.dataItem)}
                                          </div>
                                      </div>
                                  ) : undefined
                            : null
                    }
                    onScroll={onGridScroll}
                >
                    {!showFirstColumn ? (
                        <GridColumn
                            key="custom"
                            field="custom"
                            orderIndex={0}
                            width={widthFirstExtraColumn}
                            locked
                            cells={{
                                headerCell: ({ onClick, title, children, thProps }: any) => (
                                    <HeaderThElement
                                        columnId={thProps?.columnId || ''}
                                        {...thProps}
                                        className={thProps?.className}
                                    >
                                        <a id="custom" onClick={onClick} className="header-cell">
                                            <span className="header-cell-title">{title}</span>
                                            {title && title !== '' && children}
                                        </a>
                                    </HeaderThElement>
                                ),
                                data: (props: any) => (
                                    <td
                                        className="custom-cell k-grid-content-sticky"
                                        style={{ left: 0, position: 'sticky' }}
                                    >
                                        <div
                                            className={`custom-cell-flex-wrapper ${
                                                recordHasDetails(props.dataItem) ? 'with-details' : ''
                                            } 
                                            ${showCheckbox ? 'checkbox' : ''}`}
                                        >
                                            <div className="actions-checkbox">
                                                {rowActions && rowActions(props.dataItem) && (
                                                    <div
                                                        className={`actions-wrapper ${!detail ? 'without-detail' : ''}`}
                                                    >
                                                        <ActionsMenu
                                                            actions={rowActions(props.dataItem)}
                                                            customClassName="mt"
                                                            anchorOrigin={{
                                                                vertical: 'top',
                                                                horizontal: 'right',
                                                            }}
                                                            transformOrigin={{
                                                                vertical: 'top',
                                                                horizontal: 'left',
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                                {showCheckbox && (
                                                    <Checkbox
                                                        rounded={null}
                                                        className="cell-checkbox border-bottom"
                                                        checked={props.dataItem.checked}
                                                        onChange={() => {
                                                            if (setOwner) {
                                                                if (
                                                                    setOwner(
                                                                        props.dataItem.owner,
                                                                        checkedRows,
                                                                        !props.dataItem.checked,
                                                                    )
                                                                ) {
                                                                    props.dataItem.checked = !props.dataItem.checked;
                                                                    handleChangeCheckedRows(props.dataItem);
                                                                } else {
                                                                    const array = [...checkedRows];
                                                                    setCheckedRows(array);
                                                                }
                                                            } else {
                                                                props.dataItem.checked = !props.dataItem.checked;
                                                                handleChangeCheckedRows(props.dataItem);
                                                            }
                                                        }}
                                                        size="medium"
                                                    />
                                                )}
                                            </div>
                                            {recordHasDetails(props.dataItem) && (
                                                <div
                                                    className={`expand-btn ${
                                                        expandedItems[props.dataItem.id] ? 'expanded' : 'non-expanded'
                                                    }`}
                                                    onClick={() => {
                                                        expandChange(props);
                                                    }}
                                                    data-testid="expand-button"
                                                >
                                                    <FontAwesomeIcon
                                                        icon={['fal', 'chevron-down']}
                                                        className="icon down-icon"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                ),
                            }}
                            reorderable={false}
                            sortable={false}
                            resizable={false}
                        />
                    ) : null}
                    {columnsArray.map((column: any) => {
                        const width = column.minWidth
                            ? setWidth(
                                  column.minWidth,
                                  column.field,
                                  gridElement,
                                  columnsArray,
                                  widthFirstExtraColumn,
                                  column.lockedByDefault,
                              )
                            : column.width;
                        const childrenSumWidth =
                            column.children?.reduce((sum: number, item: any) => sum + item.minWidth || item.width, 0) +
                            widthFirstExtraColumn;
                        if (column.show) {
                            return (
                                <GridColumn
                                    {...column}
                                    minWidth={column.locked ? column.minWidth : 5}
                                    key={`${column.field}`}
                                    resizable={!column.locked}
                                    field={column.field}
                                    title={column.title || column.label || column.field}
                                    locked={column.locked}
                                    sortable={!column.notSortable}
                                    filterable={!column.notFilterable}
                                    width={width}
                                    children={
                                        column.children &&
                                        column.children.length &&
                                        column.children
                                            .filter((el: any) => el.show !== false)
                                            .map((item: any) => (
                                                <GridColumn
                                                    resizable={!item.locked}
                                                    key={item.field}
                                                    sortable={!item.notSortable}
                                                    filterable={!item.notFilterable}
                                                    width={
                                                        item.minWidth
                                                            ? setChildWidth(
                                                                  item.minWidth,
                                                                  column.children.length,
                                                                  width,
                                                                  childrenSumWidth,
                                                                  gridElement?.offsetWidth < minGridWidth,
                                                              )
                                                            : item.minWidth || item.width
                                                    }
                                                    cells={{
                                                        ...item.cells,
                                                        headerCell: (headerCellProps: GridCustomHeaderCellProps) => (
                                                            <HeaderThElement
                                                                columnId={headerCellProps.thProps?.columnId || ''}
                                                                {...headerCellProps.thProps}
                                                                className={headerCellProps.thProps?.className}
                                                            >
                                                                <>
                                                                    {!item.notFilterable && (
                                                                        <div
                                                                            data-testid={`column-heading-${
                                                                                column.id || column.field
                                                                            }`}
                                                                        >
                                                                            <GridColumnMenuWrapper
                                                                                {...headerCellProps.columnMenuWrapperProps}
                                                                                column={item}
                                                                                columnMenu={columnMenus.get(item.field)}
                                                                            />
                                                                        </div>
                                                                    )}
                                                                    <a
                                                                        id={`sub-title-${item.id || item.field}`}
                                                                        onClick={headerCellProps.onClick}
                                                                        className={`header-cell ${
                                                                            item.headerClass || ''
                                                                        } 
                                                            ${
                                                                item.notSortable && item.notFilterable
                                                                    ? 'full-width'
                                                                    : ''
                                                            }`}
                                                                    >
                                                                        <span
                                                                            className={`header-cell-title ${
                                                                                item.isWrapHeader ? 'wrap-text' : ''
                                                                            }`}
                                                                        >
                                                                            {headerCellProps.title}
                                                                        </span>
                                                                        {isFiltered(item.filterKey || item.field) && (
                                                                            <FontAwesomeIcon
                                                                                icon={['fas', 'filter']}
                                                                                className="icon-smaller"
                                                                            />
                                                                        )}
                                                                        {headerCellProps.children}
                                                                    </a>
                                                                </>
                                                            </HeaderThElement>
                                                        ),
                                                    }}
                                                    {...item}
                                                    minWidth={column.locked ? item.minWidth : 5}
                                                />
                                            ))
                                    }
                                    cells={{
                                        ...column.cells,
                                        headerCell: (headerCellProps: GridCustomHeaderCellProps) => (
                                            <HeaderThElement
                                                columnId={headerCellProps.thProps?.columnId || ''}
                                                {...headerCellProps.thProps}
                                                className={headerCellProps.thProps?.className}
                                            >
                                                <a
                                                    title={column.tooltipValue}
                                                    id={`title-${column.id || column.field}`}
                                                    onClick={() => {
                                                        headerCellProps.onClick();
                                                    }}
                                                    className={`header-cell ${column.headerClass || ''}
                                                    ${column.notSortable && column.notFilterable ? 'full-width' : ''}`}
                                                >
                                                    <span
                                                        className={`k-w-full ${column.isWrapHeader ? 'wrap-text' : ''}`}
                                                        title={column.tooltipValue}
                                                    >
                                                        {headerCellProps.children}
                                                    </span>
                                                    {isFiltered(column.filterKey || column.field) && (
                                                        <FontAwesomeIcon
                                                            icon={['fas', 'filter']}
                                                            className="icon-smaller"
                                                        />
                                                    )}
                                                </a>
                                                {!hideColumnMenu && !column.children && (
                                                    <div data-testid={`column-heading-${column.id || column.field}`}>
                                                        <GridColumnMenuWrapper
                                                            {...headerCellProps.columnMenuWrapperProps}
                                                            column={column}
                                                            columnMenu={columnMenus.get(column.field)}
                                                        />
                                                    </div>
                                                )}
                                            </HeaderThElement>
                                        ),
                                    }}
                                />
                            );
                        }

                        return null;
                    })}
                </Grid>
            </Tooltip>
            {showPager && (
                <Pager
                    page={currentPage}
                    amountOfPages={5}
                    pageSize={pageSize}
                    customClassname="bottom-pager"
                    total={filteredItems && filteredItems.length}
                    onChange={(page: number) => setCurrentPage(page)}
                    onSelectPageSize={(amount: number) => setPageSize(amount)}
                />
            )}
        </div>
    );
};
export default ExpandableGrid;
